import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import GeneralInvestmentDetails from './general-information';
import BoxedContent from 'ui/molecules/boxed-content';
import LoadingRing from 'ui/atoms/loading-ring';
import ActionButtons from 'ui/molecules/action-buttons';
import useApiCall from 'hooks/use-api-call';
import { AdminApi } from 'api/apis';
import useTransactionLink from 'hooks/use-transaction-link';
import { AdminInvestmentDetails, AdminInvestor, InvestmentAcquisitionTypeRecipientEnum } from 'api';
import DocumentsTable from 'src/libraries/document-table';
import StudioInvestmentPaymentDetails from './payment-details';
import StudioInvestmentTaxData from './tax-data';
import TransactionsTable from './transactions-table';
import KycAndCompliance, { KycAndComplianceType } from 'apps/issuer/shared/kyc-and-compliance-view';
import Link from 'core/routing/link';
import StudioInvestmentInvestorDetails from 'src/apps/issuer/shared/studio-investment-investor-details';
import Grid, { Col } from 'ui/atoms/grid';
import CreateRegistryStatement from './create-registry-statement';
import Section from 'ui/atoms/section';
import Spacer from 'ui/atoms/spacer';
import Header from 'ui/atoms/header';
import {
  hasDistributionManageDataPermission,
  hasDistributionViewDataPermission,
} from 'apps/issuer/helpers/distributionPlatformPermissions';
import { useCurrentUserSelector } from 'core/auth/hooks';

interface IssuerInvestmentDetailsPageProps {
  investmentId: string;
  productId: string;
}

const IssuerInvestmentDetailsPage: FunctionComponent<IssuerInvestmentDetailsPageProps> = ({
  investmentId,
  productId,
}) => {
  const [investor, setInvestor] = useState<AdminInvestor>();
  const [investment, setInvestment] = useState<AdminInvestmentDetails>();
  const [forceDataLoad, setForceDataLoad] = useState(false);

  const { currentUser } = useCurrentUserSelector();

  const { withApi, makeAuthenticatedApi } = useApiCall();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const loadData = useCallback(() => {
    withApi(async () => {
      const investment = await adminApi.adminInvestmentsRetrieve({
        id: investmentId,
      });
      const investor = await adminApi.adminInvestorsRetrieve({
        id: investment.investor.id,
      });

      setInvestment(investment);

      setInvestor(investor);
    });
  }, [withApi, setInvestment, setInvestor, investmentId]);

  useEffect(() => {
    loadData();
  }, [loadData, investmentId]);

  const { transactionLink, loading: transactionLinkLoading } = useTransactionLink(
    investment?.tokenTransactionHash,
    investment?.token?.tokenizationInfo?.network,
    investment?.token?.tokenizationInfo?.networkType,
  );

  const onRegistryStatementCreated = useCallback(() => {
    withApi(async () => {
      const investment = await adminApi.adminInvestmentsRetrieve({
        id: investmentId,
      });
      setInvestment(investment);
      setForceDataLoad(!forceDataLoad);
    });
  }, []);

  if (!investment || !investor || transactionLinkLoading)
    return (
      <WideContent title={<Translate name="dashboardIssuerInvestmentDetails.titleNoContent" />}>
        <LoadingRing />
      </WideContent>
    );

  let {
    acceptedAt,
    paidAt,
    signedAt,
    investor: minimalInvestor,
    canCancel,
    cancellationImpediments,
    agio,
    disagio,
    accruedInterest,
    cancellationDate,
    cancellationDeadline,
    exemptionOrderAmount,
    exemptionOrderDate,
    investmentTotal,
    investmentTotalFulfilled,
    remainingAmount,
    paymentTotal,
    tokenPrice,
    acquisitionType,
    voucher,
    investmentCompliance,
    entryType,
  } = investment;

  const canManageData = hasDistributionManageDataPermission(
    currentUser?.associatedDistributionPlatforms?.filter(
      (platform) => platform.id === investor?.distributionPlatformId,
    ) || null,
  );

  const canSeeDocuments = hasDistributionViewDataPermission(
    currentUser?.associatedDistributionPlatforms?.filter(
      (platform) => platform.id === investor?.distributionPlatformId,
    ) || null,
  );

  return (
    <>
      <WideContent
        title={<Translate name="dashboardIssuerInvestmentDetails.title" args={[investment.token.name]} />}
        titleSpacing="larger"
      >
        <BoxedContent releaseSpaceWhenTitleIsEmpty={true} title={<Translate name="common.generalInformation" />}>
          <GeneralInvestmentDetails
            investorName={minimalInvestor.investorName}
            investmentId={investment.id}
            status={investment?.frontendStatus}
            acceptedDate={acceptedAt}
            signedAt={signedAt}
            paymentDate={paidAt}
            transactionUrl={transactionLink}
            investmentTotalFulfilled={investmentTotalFulfilled}
            investmentTotal={investmentTotal}
            tokenPrice={tokenPrice}
            remainingAmount={remainingAmount}
            acquisitionType={acquisitionType}
            setInvestment={setInvestment}
            canCancelReasons={canCancel}
            cancellationImpediments={cancellationImpediments}
            loadData={loadData}
            cancellationDeadline={cancellationDeadline}
            cancellationDate={cancellationDate}
            hasManageDataPermission={canManageData}
            entryType={entryType}
            distributionPlatformId={investor.distributionPlatformId}
            hasFrontend={investor.distributionPlatform.hasFrontend}
          />
        </BoxedContent>
        <Section>
          <Grid>
            {!investment.token.ewpg && (
              <Col width={6}>
                <Header size="small" spacing="medium">
                  <Translate name="studioInvestmentTaxData.title" />
                </Header>
              </Col>
            )}
            <Col width={6}>
              <Header size="small" spacing="medium">
                <Translate name="studioInvestmentPaymentDetails.title" />
              </Header>
            </Col>
          </Grid>
          <Grid>
            {!investment.token.ewpg && (
              <Col width={6}>
                <BoxedContent releaseSpaceWhenTitleIsEmpty={true} fullHeight>
                  <StudioInvestmentTaxData
                    taxInformation={{
                      taxIdentificationNumber: investor.person?.taxInformation?.taxIdentificationNumber,
                      exemptionOrderSum: exemptionOrderAmount,
                      exemptionOrderDate: exemptionOrderDate,
                    }}
                  />
                </BoxedContent>
              </Col>
            )}
            <Col width={6}>
              <BoxedContent releaseSpaceWhenTitleIsEmpty={true} fullHeight>
                <StudioInvestmentPaymentDetails
                  agio={agio}
                  disagio={disagio}
                  accruedInterest={accruedInterest}
                  investmentTotalFulfilled={investmentTotalFulfilled}
                  canCancelReasons={canCancel}
                  token={investment.token}
                  investmentId={investmentId}
                  loadData={loadData}
                  paymentTotal={paymentTotal}
                  tokenPrice={tokenPrice}
                  cancellationDate={cancellationDate}
                  acceptedDate={acceptedAt}
                  investmentCompliance={investmentCompliance}
                  voucher={voucher}
                  hasManageDataPermission={canManageData}
                  useUnits={false}
                />
              </BoxedContent>
            </Col>
          </Grid>
        </Section>
        {acquisitionType !== InvestmentAcquisitionTypeRecipientEnum.HANDOVER && (
          <BoxedContent title={<Translate name="kycComplianceDetails.title" />} releaseSpaceWhenTitleIsEmpty={true}>
            <KycAndCompliance
              type={KycAndComplianceType.INVESTMENT}
              kycStatus={investment.kycStatus}
              hasCompliance={investment.usesEffecta}
              complianceExportRequired={investment.complianceExportRequired}
              comment={investment.investmentCompliance?.comment}
              complianceStatus={investment.investmentCompliance?.status}
              complianceDate={investment.investmentCompliance?.complianceDate}
              hasManageDataPermission={canManageData}
              canCancelReasons={canCancel}
              cancellationDate={cancellationDate}
              investmentId={investmentId}
              hasFrontend={investment.investor.distributionPlatform.hasFrontend}
              investorId={investor.id}
              loadData={loadData}
            />
          </BoxedContent>
        )}
      </WideContent>
      <Spacer y={8} />
      <Section>
        <WideContent title={<Translate name="dashboard.investorTransactions" />} titleSpacing="medium" ignorePageTitle>
          <TransactionsTable investmentId={investmentId} productId={productId} />
        </WideContent>
      </Section>
      <Section>
        <WideContent
          title={<Translate name="dashboard.issuerDocuments" />}
          ignorePageTitle
          headerPanel={
            canManageData &&
            investment.token.ewpg &&
            investment.paidAt && (
              <CreateRegistryStatement
                investmentId={investment.id}
                onRegistryStatementCreated={onRegistryStatementCreated}
                investorHasUser={!!investor?.hasUser}
              />
            )
          }
          titleSpacing="medium"
        >
          <DocumentsTable
            investmentId={investmentId}
            forceDataLoad={forceDataLoad}
            setForceDataLoad={setForceDataLoad}
            hasPermission={canSeeDocuments}
            skipFilterUrlSync={true}
          />
        </WideContent>
      </Section>
      <WideContent>
        <Header size="small" spacing="medium">
          <Translate name="studioInvestmentInvestorDetails.title" />
        </Header>
        <BoxedContent releaseSpaceWhenTitleIsEmpty={true}>
          <StudioInvestmentInvestorDetails
            name={minimalInvestor.investorName}
            email={minimalInvestor.email}
            activeInvestmentsNo={investor.activeInvestmentsNo}
            activeInvestmentsSum={investor.activeInvestmentsSum}
            entryType={investor.entryType}
          />
          <ActionButtons>
            <Link variant="secondary" to={`/dashboard/investor/${investor.id}`}>
              <Translate name="studioInvestmentInvestorDetails.goToInvestor" />
            </Link>
          </ActionButtons>
        </BoxedContent>
        <Link icon="arrow-left" to="/dashboard/investments">
          <Translate as="span" name="dashboardIssuerInvestmentDetails.goToInvestments" />
        </Link>
      </WideContent>
    </>
  );
};

export default WithAuthenticatedPage(IssuerInvestmentDetailsPage, AppType.ISSUER);
